// Response schema for the go-live tracker import (plan §4 POST /imports/tracker, spec §5.10, Q36).
// Shapes follow apps/api/src/modules/imports/service.ts and types.ts. Owner only, so the values
// are never redacted, but a value can be a long note or decision text: caps leave room for it.
import { z } from 'zod';

const MAX_TEXT = 120_000;
const MAX_LABEL = 3_000;
// An id the portal does not know comes straight from a tracker cell, uncapped by the API (an Excel
// cell holds up to 32,767 characters; a crafted file more). One stray note pasted into the Task ID
// column must not make the whole report unreadable, least of all after an apply (review I-R1).
const MAX_CELL = MAX_TEXT;
const SHA256_HEX = 64;

/** The phrase the API asks for before an apply over existing portal state (Q36). */
export const TRACKER_FORCE_PHRASE = 'OVERWRITE PORTAL STATE';

/** The API refuses a larger tracker file (413). */
const MAX_MB = 10;
const BYTES_PER_MB = 1_048_576;
export const TRACKER_MAX_BYTES = MAX_MB * BYTES_PER_MB;

const count = z.number().int().min(0);
const label = z.string().max(MAX_LABEL);
const cell = z.string().max(MAX_CELL);
const valueSchema = z.union([z.string().max(MAX_TEXT), z.number(), z.null()]);

export const trackerChangeSchema = z.object({
  id: cell,
  field: label,
  from: valueSchema,
  to: valueSchema,
});
export type TrackerChange = z.infer<typeof trackerChangeSchema>;

export const trackerInvalidSchema = z.object({
  row: z.number().int().min(0),
  column: label,
  message: z.string().max(MAX_TEXT),
});
export type TrackerInvalid = z.infer<typeof trackerInvalidSchema>;

export const trackerTabSchema = z.object({
  tab: label,
  found: z.boolean(),
  rows: count,
  missingColumns: z.array(label),
  unknownIds: z.array(cell),
  missingIds: z.array(cell),
  changes: z.array(trackerChangeSchema),
  invalid: z.array(trackerInvalidSchema),
});
export type TrackerTab = z.infer<typeof trackerTabSchema>;

export const trackerReportSchema = z.object({
  dryRun: z.boolean(),
  applied: z.boolean(),
  stateExists: z.boolean(),
  fileSha256: z.string().length(SHA256_HEX),
  tabs: z.array(trackerTabSchema),
  totals: z.object({ changes: count, unknownIds: count, invalid: count }),
});
export type TrackerReport = z.infer<typeof trackerReportSchema>;
